import { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import { uniswapWethUsdcPoolAddress } from '@silo/contract-addresses';
import {
  BigIntWithDecimals,
  ChainIdLegacy,
  ChecksumAddress,
  createBigNumberWithDecimals,
  createProtocolInstanceValueGetter,
  getChecksumAddressOrThrow,
  isEnvVarOn,
  SupportedChainValue,
  USD_DECIMALS,
} from '@silo/types';
import { flow } from 'fp-ts/lib/function';

/**
 * base asset quote token pool
 *
 * These are uniswap WETH/USDC pools. To add a new chain, find the largest pool for that chain using a tool like https://www.geckoterminal.com/.
 *
 * 1) filter by the new chain
 * 2) filter by Uniswap V3 as the DEX
 * 3) sort by liquidity descending, and pick the first WETH-USDC pool
 */
export const BASE_PRICE_POOL: SupportedChainValue<ChecksumAddress> = {
  [ChainIdLegacy.MAINNET]: getChecksumAddressOrThrow(uniswapWethUsdcPoolAddress.ethereum),
  [ChainIdLegacy.LOCAL]: getChecksumAddressOrThrow(uniswapWethUsdcPoolAddress.local),
  [ChainIdLegacy.ARBITRUM]: getChecksumAddressOrThrow(uniswapWethUsdcPoolAddress.arbitrum),
  [ChainIdLegacy.OPTIMISM]: getChecksumAddressOrThrow(uniswapWethUsdcPoolAddress.optimism),
  [ChainIdLegacy.BASE]: getChecksumAddressOrThrow(uniswapWethUsdcPoolAddress.base),
  [ChainIdLegacy.ANVIL]: getChecksumAddressOrThrow(uniswapWethUsdcPoolAddress.anvil),
};

export const CHAINLINK_QUOTE_PRICE_DECIMALS: SupportedChainValue<number> = {
  [ChainIdLegacy.MAINNET]: 8,
  [ChainIdLegacy.ARBITRUM]: 8,
  [ChainIdLegacy.OPTIMISM]: 8,
  [ChainIdLegacy.BASE]: 8,
  [ChainIdLegacy.LOCAL]: 8,
  [ChainIdLegacy.ANVIL]: 8,
};

/**
 * If a withdrawal or repay value is within this value of the max, then it will
 * be treated as a full amount transaction, and the position will be closed.
 */
export const FULL_AMOUNT_THRESHOLD = createBigNumberWithDecimals(
  parseUnits('1', USD_DECIMALS) as BigNumber,
  USD_DECIMALS
);

const DEFAULT_MIN_DEPOSIT = '1';
/**
 * Minimum deposit amount configured as a string USD amount
 *
 * ```ts
 * '10' // => $10
 * ```
 */
export const getMinDepositAmount = flow(
  createProtocolInstanceValueGetter(DEFAULT_MIN_DEPOSIT, {
    mainnetLegacy: DEFAULT_MIN_DEPOSIT,
    mainnetLlama: DEFAULT_MIN_DEPOSIT,
    mainnetGrand: DEFAULT_MIN_DEPOSIT,
    arbitrum: DEFAULT_MIN_DEPOSIT,
    optimism: DEFAULT_MIN_DEPOSIT,
    base: DEFAULT_MIN_DEPOSIT,
    lrtbase: '100',
    local: DEFAULT_MIN_DEPOSIT,
    anvil: DEFAULT_MIN_DEPOSIT,
  }),
  (amountStr): BigIntWithDecimals => ({
    value: parseUnits(amountStr, USD_DECIMALS).toBigInt(),
    decimals: USD_DECIMALS,
  })
);

const isStaging = isEnvVarOn(() => process.env.NEXT_PUBLIC_IS_STAGING);

const DEFAULT_MIN_BORROW = '1';
/**
 * Minimum borrow amount configured as a string USD amount
 *
 * ```ts
 * '10' // => $10
 * ```
 */
export const getMinBorrowAmount = flow(
  createProtocolInstanceValueGetter(DEFAULT_MIN_BORROW, {
    mainnetLegacy: DEFAULT_MIN_BORROW,
    mainnetLlama: DEFAULT_MIN_BORROW,
    mainnetGrand: DEFAULT_MIN_BORROW,
    arbitrum: '10',
    optimism: isStaging ? '0.1' : '50',
    base: isStaging ? '0.1' : '50',
    lrtbase: '100',
    local: DEFAULT_MIN_BORROW,
    anvil: DEFAULT_MIN_BORROW,
  }),
  (amountStr): BigIntWithDecimals => ({
    value: parseUnits(amountStr, USD_DECIMALS).toBigInt(),
    decimals: USD_DECIMALS,
  })
);
